import { useNavigate } from 'react-router';
import { Layers } from 'lucide-react';
import { Pagination } from '@/components/ui/pagination';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CarryStatStrip } from '@/components/funds-carry/funds/CarryStatStrip';
import { VestingStateBadge } from '@/components/funds-carry/funds/CarryVestingBar';
import { CarryFundNameCell } from '@/components/funds-carry/funds/CarryFundNameCell';
import {
  MUTED_NUM_CELL,
  NUM_CELL,
  VESTING_BAR_CLASSES,
  formatShare,
  formatUnits,
  poolShare,
  unitTotals,
  unvestedUnits,
  vestedShare,
  vestingPeriod,
  vestingState,
} from '@/components/funds-carry/funds/carryFundValues';
import {
  PAID_IN_STAT,
  vestingStats,
} from '@/components/funds-carry/funds/carryUnitStats';
import { useClientPagination } from '@/hooks/useClientPagination';
import { cn } from '@/lib/utils';
import { formatDate } from '@/utils/formatters';
import type { CarryHolding, CarryHoldings } from '../services/carryService';
import { portalCarryFundPath } from '../carryPaths';

function VestingCell({
  holding,
  today,
}: {
  holding: CarryHolding;
  today: string;
}) {
  const state = vestingState(holding);
  if (state === 'NO_PERIOD') {
    return <span className="text-muted-foreground">—</span>;
  }
  const share = vestedShare(holding);
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-3">
        <Progress
          value={share * 100}
          className={cn('h-1.5 w-28', VESTING_BAR_CLASSES[state])}
        />
        <span className="text-sm">{formatShare(share)}</span>
        <VestingStateBadge state={state} />
      </div>
      <div className="text-xs text-muted-foreground">
        {state === 'FROZEN'
          ? `Since ${formatDate(holding.frozen_on)}`
          : vestingPeriod(holding)}
      </div>
      {holding.as_of !== today && (
        <div className="text-xs text-muted-foreground">
          As of {formatDate(holding.as_of)}
        </div>
      )}
    </div>
  );
}

export function CarryFundsTab({ data }: { data: CarryHoldings }) {
  const { holdings, as_of: today } = data;
  const navigate = useNavigate();
  const totals = unitTotals(holdings);
  const { pageItems: shown, paginationProps } = useClientPagination(
    holdings,
    10
  );

  if (holdings.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        You hold no carry units yet.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <CarryStatStrip
        stats={[
          {
            icon: Layers,
            label: 'Units',
            value: formatUnits(totals.units),
            hint: `in ${holdings.length} ${holdings.length === 1 ? 'fund' : 'funds'}`,
          },
          PAID_IN_STAT,
          ...vestingStats(totals),
        ]}
      />

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fund</TableHead>
              <TableHead className={NUM_CELL}>Units</TableHead>
              <TableHead className={NUM_CELL}>% of pool</TableHead>
              <TableHead className={NUM_CELL}>Paid in</TableHead>
              <TableHead>Vesting</TableHead>
              <TableHead className={NUM_CELL}>Vested</TableHead>
              <TableHead className={NUM_CELL}>Unvested</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((holding) => (
              <TableRow
                key={holding.fund_id}
                onClick={() => navigate(portalCarryFundPath(holding.fund_id))}
                className="cursor-pointer hover:bg-muted/50"
              >
                <CarryFundNameCell
                  name={holding.fund_name}
                  displayName={holding.display_name}
                />
                <TableCell className={NUM_CELL}>
                  {formatUnits(holding.units)}
                </TableCell>
                <TableCell className={MUTED_NUM_CELL}>
                  {formatShare(
                    poolShare(holding.units, holding.total_carry_units),
                    1
                  )}
                </TableCell>
                <TableCell className={MUTED_NUM_CELL}>—</TableCell>
                <TableCell>
                  <VestingCell holding={holding} today={today} />
                </TableCell>
                <TableCell className={NUM_CELL}>
                  {formatUnits(holding.vested_units)}
                </TableCell>
                <TableCell className={MUTED_NUM_CELL}>
                  {formatUnits(unvestedUnits(holding))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Pagination
        {...paginationProps}
        showSizeChanger
        pageSizeOptions={[10, 25, 50]}
      />
    </div>
  );
}
